import React, { useEffect, useState } from 'react';
import { Card, Container, Box, Typography, CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ConversationTable from './ConversationTable';
import SearchBar from './SearchBar';
import { convertConversations } from './chatgptUtils';
import { ReactComponent as Logo } from './logo.svg';

import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// For drag-and-drop
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

const App = () => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [fileUploaded, setFileUploaded] = useState(false); // Track if file is uploaded

  const handleFileUpload = (event) => {
    let file;
    if (event.target.files) {
      file = event.target.files[0];
    } else {
      file = event.dataTransfer.files[0];
    }

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          const conversationData = convertConversations(jsonData);

          setConversations(conversationData);
          setFilteredConversations(conversationData);
          setFileUploaded(true);
        } catch (error) {
          console.error('Error parsing JSON', error);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFileUpload(event);
  };

  const handleSearch = (searchText) => {
    const lowercasedFilter = searchText.toLowerCase();
    const filteredData = conversations.filter((conversation) => {
      const titleMatch = conversation.title
        .toLowerCase()
        .includes(lowercasedFilter);
      const messagesMatch = conversation.messages.some((msg) =>
        msg.text.toLowerCase().includes(lowercasedFilter)
      );
      return titleMatch || messagesMatch;
    });
    setFilteredConversations(filteredData);
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      // Enable GA4
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GA_TRACKING_ID}`;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${process.env.REACT_APP_GA_TRACKING_ID}');
      `;
      document.head.appendChild(script2);
    }
  }, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container>
        <Box my={4}>
          {/* Branding Area */}
          <Box
            my={4}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Logo style={{ width: '128px', height: 'auto' }} />{' '}
            <Typography variant="h4" component="h1" gutterBottom ml={2}>
                ChatGPT Conversations History
              </Typography>
          </Box>

          {/* Upload Area */}
          {!fileUploaded && (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '100%',
                height: 'auto',
                minHeight: '20rem',
                border: '2px dashed #aaa',
                borderRadius: '10px',
                backgroundColor: '#2c2c2e',
                cursor: 'pointer',
                textAlign: 'center',
                padding: '1rem',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: '#444',
                },
              }}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  maxWidth: '100%',
                }}
              >
                <Typography variant="h6" color="textSecondary">
                  Drop your file here, or{' '}
                  <span style={{ color: '#fff', fontWeight: 'bold' }}>
                    browse
                  </span>{' '}
                  to load.
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    marginTop: '1rem',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    maxWidth: '90%',
                    wordWrap: 'break-word',
                  }}
                >
                  NOTE: Your data stays only in your browser, on your device. It
                  is not uploaded to any server, so it stays completely private
                  and secure. Once you close this tab, the data will be gone.
                </Typography>
              </Box>
              <input
                id="fileInput"
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
            </Box>
          )}

          {!fileUploaded && (
            <Box my={4}>
              {/* Instructions Section */}
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                textAlign="center"
                sx={{ color: '#FFFFFF', mb: 4 }}
              >
                Step-by-Step Guide
              </Typography>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={3}
                mt={2}
                sx={{ maxWidth: '700px', margin: '0 auto' }}
              >
                {/* Step 1 */}
                <Card
                  sx={{
                    width: '100%',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#2c2c2e',
                    borderRadius: '10px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)', // Add shadow for depth
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)', // Hover effect
                    },
                  }}
                >
                  <DownloadIcon
                    sx={{ fontSize: 40, color: '#9c41f1', marginRight: '20px' }}
                  />
                  <Typography variant="body1" color="textPrimary">
                    <strong>Step 1:</strong> In ChatGPT, go to{' '}
                    <strong>Settings</strong>, go to tab{' '}
                    <strong>"Data Controls"</strong> and next to{' '}
                    <strong>"Export Data"</strong> click on{' '}
                    <strong>"Export"</strong>.
                  </Typography>
                </Card>

                {/* Step 2 */}
                <Card
                  sx={{
                    width: '100%',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#2c2c2e',
                    borderRadius: '10px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  <EmailIcon
                    sx={{ fontSize: 40, color: '#7d62f3', marginRight: '20px' }}
                  />
                  <Typography variant="body1" color="textPrimary">
                    <strong>Step 2:</strong> Check your email for the message
                    from ChatGPT with a link to download the exported data.
                  </Typography>
                </Card>

                {/* Step 3 */}
                <Card
                  sx={{
                    width: '100%',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#2c2c2e',
                    borderRadius: '10px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  <FileDownloadIcon
                    sx={{ fontSize: 40, color: '#6083f4', marginRight: '20px' }}
                  />
                  <Typography variant="body1" color="textPrimary">
                    <strong>Step 3:</strong> Follow the instructions in the
                    email to download your data.
                  </Typography>
                </Card>

                {/* Step 4 */}
                <Card
                  sx={{
                    width: '100%',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#2c2c2e',
                    borderRadius: '10px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  <FolderZipIcon
                    sx={{ fontSize: 40, color: '#40a5f6', marginRight: '20px' }}
                  />
                  <Typography variant="body1" color="textPrimary">
                    <strong>Step 4:</strong> Unzip the downloaded file, and find
                    the <strong>conversations.json</strong> file.
                  </Typography>
                </Card>

                {/* Step 5 */}
                <Card
                  sx={{
                    width: '100%',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#2c2c2e',
                    borderRadius: '10px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  <CloudUploadIcon
                    sx={{ fontSize: 40, color: '#1cccf8', marginRight: '20px' }}
                  />
                  <Typography variant="body1" color="textPrimary">
                    <strong>Step 5:</strong> Return to this page, drag and drop
                    the <strong>conversations.json</strong> file into the upload
                    area above, or browse to upload it.
                  </Typography>
                </Card>
              </Box>
            </Box>
          )}

          {/* Show search bar and conversation table only after file is uploaded */}
          {fileUploaded && (
            <>
              <SearchBar onSearch={handleSearch} />
              <ConversationTable conversations={filteredConversations} />
            </>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default App;
